import { useEffect, useMemo, useState } from "react";
import {
  Mail,
  Trash2,
  Loader2,
  Check,
  MailOpen,
  FileText,
  MessageSquare,
  Phone,
  User,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listSubmissions, markSubmissionRead, deleteSubmission } from "@/lib/api";

const TIPO_LABEL = {
  preventivo: "Preventivo",
  contatto: "Contatto",
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterTipo, setFilterTipo] = useState("all"); // all | preventivo | contatto
  const [filterUnread, setFilterUnread] = useState(false);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    listSubmissions({
      tipo: filterTipo === "all" ? undefined : filterTipo,
      unread: filterUnread,
    })
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTipo, filterUnread]);

  const openDetail = async (s) => {
    setDetail(s);
    if (!s.is_read) {
      try {
        await markSubmissionRead(s.id, true);
        setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, is_read: true } : x)));
      } catch {}
    }
  };

  const toggleRead = async (s) => {
    try {
      await markSubmissionRead(s.id, !s.is_read);
      setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, is_read: !s.is_read } : x)));
    } catch (e) {
      setError(e.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSubmission(deleteTarget.id);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setDeleteTarget(null);
      if (detail?.id === deleteTarget.id) setDetail(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const unreadCount = useMemo(() => items.filter((s) => !s.is_read).length, [items]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0B1E36]">Richieste dal sito</h2>
          <p className="text-sm text-[#64748B]">
            {items.length} {items.length === 1 ? "richiesta" : "richieste"}
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#FF9500]/10 text-[#FF9500] rounded-full text-xs font-semibold">
                {unreadCount} da leggere
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {["all", "preventivo", "contatto"].map((v) => (
          <button
            key={v}
            onClick={() => setFilterTipo(v)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filterTipo === v
                ? "bg-[#0B1E36] text-white border-[#0B1E36]"
                : "bg-white text-[#0B1E36] border-[#E2E8F0] hover:border-[#0B1E36]"
            }`}
            data-testid={`filter-tipo-${v}`}
          >
            {v === "all" ? "Tutte" : TIPO_LABEL[v]}
          </button>
        ))}
        <button
          onClick={() => setFilterUnread((u) => !u)}
          className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            filterUnread
              ? "bg-[#FF9500] text-white border-[#FF9500]"
              : "bg-white text-[#0B1E36] border-[#E2E8F0] hover:border-[#FF9500]"
          }`}
          data-testid="filter-unread"
        >
          Solo non lette
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0B1E36]" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-[#64748B]">
          Nessuna richiesta con questi filtri.
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <Card
              key={s.id}
              className={`p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow ${
                !s.is_read ? "border-l-4 border-l-[#FF9500]" : ""
              }`}
              onClick={() => openDetail(s)}
              data-testid={`submission-item-${s.id}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
                {s.tipo === "preventivo" ? (
                  <FileText className="w-5 h-5 text-[#007AFF]" />
                ) : (
                  <MessageSquare className="w-5 h-5 text-[#FF9500]" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold uppercase text-[#94A3B8]">
                    {TIPO_LABEL[s.tipo]}
                  </span>
                  {!s.is_read && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF9500]" />
                  )}
                </div>
                <p className="font-semibold text-[#0B1E36] truncate">
                  {s.nome} <span className="font-normal text-[#64748B]">· {s.email}</span>
                </p>
                <p className="text-sm text-[#64748B] truncate">{s.messaggio}</p>
              </div>

              <div className="hidden sm:block text-xs text-[#94A3B8] flex-shrink-0">
                {formatDate(s.created_at)}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleRead(s)}
                  title={s.is_read ? "Segna come non letta" : "Segna come letta"}
                >
                  {s.is_read ? (
                    <Mail className="w-4 h-4" />
                  ) : (
                    <MailOpen className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(s)}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  title="Elimina"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.tipo === "preventivo" ? (
                    <FileText className="w-5 h-5 text-[#007AFF]" />
                  ) : (
                    <MessageSquare className="w-5 h-5 text-[#FF9500]" />
                  )}
                  {TIPO_LABEL[detail.tipo]}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <InfoRow icon={User} label="Nome" value={detail.nome} />
                <InfoRow
                  icon={Mail}
                  label="Email"
                  value={<a href={`mailto:${detail.email}`} className="text-[#007AFF] hover:underline">{detail.email}</a>}
                />
                {detail.telefono && (
                  <InfoRow
                    icon={Phone}
                    label="Telefono"
                    value={<a href={`tel:${detail.telefono}`} className="text-[#007AFF] hover:underline">{detail.telefono}</a>}
                  />
                )}
                {detail.tipo_serramento && (
                  <InfoRow label="Tipo serramento" value={detail.tipo_serramento} />
                )}
                {detail.dimensioni && (
                  <InfoRow label="Dimensioni" value={detail.dimensioni} />
                )}
                {detail.quantita && (
                  <InfoRow label="Quantità" value={detail.quantita} />
                )}
                <InfoRow icon={Calendar} label="Ricevuto" value={formatDate(detail.created_at)} />

                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold mb-2">
                    Messaggio
                  </p>
                  <div className="p-4 rounded-lg bg-[#F8FAFC] text-[#334155] whitespace-pre-wrap text-sm leading-relaxed">
                    {detail.messaggio}
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-[#E2E8F0]">
                  <a
                    href={`mailto:${detail.email}?subject=Re:%20${encodeURIComponent(TIPO_LABEL[detail.tipo])}%20-%20Finestre%20e%20Colore`}
                    className="inline-flex items-center gap-2 text-[#007AFF] font-medium text-sm hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    Rispondi via email
                  </a>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        toggleRead(detail);
                        setDetail({ ...detail, is_read: !detail.is_read });
                      }}
                    >
                      {detail.is_read ? "Segna non letta" : "Segna letta"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(detail)}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Elimina
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Eliminare la richiesta di "{deleteTarget?.nome}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione è irreversibile. La richiesta verrà rimossa definitivamente dal database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 mt-0.5 text-[#94A3B8] flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold">
          {label}
        </p>
        <p className="text-sm text-[#0B1E36]">{value}</p>
      </div>
    </div>
  );
}
